import { useState, useRef, useEffect } from 'react';

const testimonials = [
  {
    text: 'I was able to find my existing carpet relatively easily with limited info and get samples to confirm my preferred color and design. Going through the selection process was smooth, and the installation was impeccable.',
    name: 'Satisfied Customer',
    role: 'Residential Client',
    rating: 5,
  },
  {
    text: 'Shopping was made easy. The team was incredibly helpful — from browsing options to the final install. The result looks incredible. Love the fact that they guided me through every step.',
    name: 'Happy Homeowner',
    role: 'Residential Client',
    rating: 5,
  },
  {
    text: 'Fast, efficient, and more pleasurable than any other experience I\'ve had with flooring companies. Their customer service is very helpful, and the quality speaks for itself.',
    name: 'Verified Client',
    role: 'Residential Client',
    rating: 5,
  },
  {
    text: 'As a business owner, finding the right flooring for commercial space is tough. Value Carpet made it easy and the end result was exactly what we needed — professional, durable, and refined.',
    name: 'Business Owner',
    role: 'Commercial Client',
    rating: 5,
  },
  {
    text: 'Everyone was very helpful, their customer service is always attentive to my needs. The website was easy to navigate, and the products are of premium quality. Superb!',
    name: 'Loyal Client',
    role: 'Repeat Client',
    rating: 5,
  },
];

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const getVisible = () => [
    active % testimonials.length,
    (active + 1) % testimonials.length,
    (active + 2) % testimonials.length,
  ];

  return (
    <section ref={ref} id="testimonials" className="py-24 lg:py-32 bg-charcoal relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-gold/[0.03] -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-gold/[0.03] translate-x-1/3 translate-y-1/3" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16"
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div>
            <span className="section-tag mb-4 block">What Clients Say</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-white leading-tight">
              Real Reviews from{' '}
              <em className="text-gold not-italic">Real Clients</em>
            </h2>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setActive((a) => (a - 1 + testimonials.length) % testimonials.length)}
              className="w-12 h-12 flex items-center justify-center rounded-full border border-white/15 text-white/60 hover:border-gold hover:text-gold transition-all cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
            </button>
            <button
              onClick={() => setActive((a) => (a + 1) % testimonials.length)}
              className="w-12 h-12 flex items-center justify-center rounded-full border border-white/15 text-white/60 hover:border-gold hover:text-gold transition-all cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {getVisible().map((idx, pos) => {
            const t = testimonials[idx];
            return (
              <article
                key={`${idx}-${pos}`}
                className={`rounded-lg p-8 flex flex-col transition-all duration-500 ${
                  pos === 0
                    ? 'bg-gold/10 border border-gold/25'
                    : 'bg-white/[0.04] border border-white/10'
                }`}
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0)' : 'translateY(32px)',
                  transition: `opacity 0.6s ease ${pos * 0.1}s, transform 0.6s ease ${pos * 0.1}s`,
                }}
              >
                <div className="flex gap-0.5 mb-6">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-gold text-sm"></i>
                    </span>
                  ))}
                </div>
                <span className="w-7 h-7 flex items-center justify-center mb-4">
                  <i className="ri-double-quotes-l text-gold/30 text-3xl"></i>
                </span>
                <p className="font-serif text-white/75 text-base leading-relaxed italic flex-1 mb-8">
                  {t.text}
                </p>
                <div className="pt-5 border-t border-white/10 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-3-line text-white/40 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <p className="text-white/40 text-xs">{t.role}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-10">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                i === active ? 'bg-gold w-6 h-2' : 'bg-white/20 w-2 h-2 hover:bg-gold/40'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
